/**
 * HTTP client — all network primitives live here.
 * Nothing domain-specific; just fetch wrappers, error handling, and config.
 */
import { secureStorage } from '../secureStorage';
import { patientStorage } from '../patientStorage';
import { safeConsole, maskCredentials } from '../piiProtection';
import { getFriendlyErrorMessage } from '../errorMapping';
import { captureStaffSession, capturePatientSession } from './sessionToken';

// ─── Environment & Base URL ───────────────────────────────────────────────────

const APP_ENV =
  (import.meta.env.VITE_APP_ENV as string | undefined) ??
  (import.meta.env.DEV ? 'dev' : 'prod');

function resolveApiBaseUrl(): string {
  let envUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;

  const hostname = window.location.hostname;
  const isIP = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname);

  // When the browser is accessing via a LAN IP in Vite dev mode, the Vite proxy
  // (which forwards /api → localhost:8080) is the only reliable path to the local
  // backend. Route through window.location.origin so the proxy is used regardless
  // of what VITE_API_BASE_URL is set to.
  if (isIP && import.meta.env.DEV) {
    return window.location.origin;
  }

  if (isIP && envUrl?.includes('localhost')) {
    envUrl = envUrl.replace('localhost', hostname);
  }

  if (envUrl) {
    return envUrl.replace(/\/api\/?$/, '');
  }

  const env = APP_ENV.toLowerCase();
  if (env === 'localhost') return 'http://localhost:8080';
  if (env === 'dev' || env === 'development' || env === 'staging')
    return 'https://dev-api.clinvea.com';
  return 'https://api.clinvea.com';
}

export const API_BASE_URL = resolveApiBaseUrl();

safeConsole.log('🌐 API URL:', API_BASE_URL);
safeConsole.log('📦 VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);
safeConsole.log('🎯 VITE_APP_ENV:', import.meta.env.VITE_APP_ENV);
if (import.meta.env.DEV) {
  safeConsole.log('📦 Environment:', APP_ENV);
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export class ApiError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Thrown (not returned) when the server responds 429. Carries the Retry-After seconds. */
export class RateLimitError extends ApiError {
  constructor(public readonly retryAfterSeconds: number) {
    super('Too many requests', 429);
    this.name = 'RateLimitError';
  }
}

// ─── Utilities ────────────────────────────────────────────────────────────────

export function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function extractFileName(contentDisposition: string | null): string | undefined {
  if (!contentDisposition) return undefined;
  const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1]);
  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1];
}

function baseHeaders(extra?: Record<string, string>): Record<string, string> {
  return {
    'X-Request-ID': generateRequestId(),
    'X-Client-Version': '1.0.0',
    'X-App-Environment': APP_ENV,
    ...extra,
  };
}

// ─── Silent-refresh-and-retry-once on 401 ──────────────────────────────────────
//
// A 401 can mean "genuinely logged out" or just "access token expired between the proactive
// background refresh timer (AuthContext/PatientAuthContext) and this particular request." The
// latter is common right around the token's natural expiry — rather than bouncing the user to
// /login for what's actually a recoverable, momentary gap, attempt exactly one silent refresh and
// retry. Concurrent 401s share a single in-flight refresh call (via the module-level promises
// below) instead of each firing their own — refresh-token rotation is single-use, so N simultaneous
// refresh attempts would have N-1 of them fail as "already rotated."
//
// Deliberately implemented as raw fetch() calls here rather than importing authModule/
// patientPortalModule's refresh() — those modules import fetchApi/fetchPatientApi from this file,
// so importing them back here would be a circular import.

let staffRefreshInFlight: Promise<boolean> | null = null;
let patientRefreshInFlight: Promise<boolean> | null = null;

async function attemptStaffRefresh(): Promise<boolean> {
  if (!staffRefreshInFlight) {
    staffRefreshInFlight = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: baseHeaders(),
          credentials: 'include',
        });
        if (!response.ok) return false;
        const body = await response.json().catch(() => null);
        // Raw fetch, so `body` is the backend response itself rather than a fetchApi envelope.
        // Wrapping it in { data } lines the shapes up so this shares one extraction with the rest.
        return captureStaffSession({ data: body }) !== null;
      } catch {
        return false;
      } finally {
        staffRefreshInFlight = null;
      }
    })();
  }
  return staffRefreshInFlight;
}

async function attemptPatientRefresh(): Promise<boolean> {
  if (!patientRefreshInFlight) {
    patientRefreshInFlight = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/patient/auth/refresh`, {
          method: 'POST',
          headers: baseHeaders(),
          credentials: 'include',
        });
        if (!response.ok) return false;
        const body = await response.json().catch(() => null);
        // See the staff refresh above for why the body is wrapped.
        return capturePatientSession({ data: body }) !== null;
      } catch {
        return false;
      } finally {
        patientRefreshInFlight = null;
      }
    })();
  }
  return patientRefreshInFlight;
}

// ─── Core fetch wrappers ──────────────────────────────────────────────────────

export async function fetchApi<T>(
  endpoint: string,
  options: RequestInit & { skipAuth?: boolean; skipBodyLog?: boolean; _retried?: boolean } = {}
): Promise<ApiResponse<T>> {
  const requestId = generateRequestId();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Request-ID': requestId,
    'X-Client-Version': '1.0.0',
    'X-App-Environment': APP_ENV,
    ...(options.headers as Record<string, string> | undefined),
  };

  const sessionToken = sessionStorage.getItem('sessionToken');
  if (sessionToken) headers['X-Session-Token'] = sessionToken;

  // Multi-branch: attach active clinic header if set.
  // TenantContextFilter on the backend validates this per-request.
  const activeClinicId = sessionStorage.getItem('active_clinic_id');
  if (activeClinicId) headers['X-Active-Clinic-Id'] = activeClinicId;

  if (options.body && !options.skipBodyLog) {
    let parsed: unknown;
    try { parsed = JSON.parse(String(options.body)); } catch { parsed = String(options.body); }
    safeConsole.log(
      `[API Request ${requestId}] ${options.method ?? 'GET'} ${endpoint}`,
      { body: maskCredentials(parsed) }
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        const isLoginEndpoint   = endpoint === '/api/auth/login';
        const isLogoutEndpoint  = endpoint === '/api/auth/logout';
        const isRefreshEndpoint = endpoint === '/api/auth/refresh';

        // One silent refresh-and-retry attempt before falling back to the redirect-to-login path
        // below. Never attempted for the auth endpoints themselves (would loop) or a request that's
        // already a retry (bounds this to exactly one attempt per original call).
        if (!isLoginEndpoint && !isLogoutEndpoint && !isRefreshEndpoint && !options._retried) {
          const refreshed = await attemptStaffRefresh();
          if (refreshed) {
            return fetchApi<T>(endpoint, { ...options, _retried: true });
          }
        }

        const isRecentAuth      = secureStorage.isRecentLogin();
        const isReadOnly        = (options?.method ?? 'GET') === 'GET';

        if (!isLoginEndpoint && !isLogoutEndpoint && !isRecentAuth && !isReadOnly) {
          secureStorage.removeAuthToken();
          setTimeout(() => { window.location.href = '/login'; }, 100);
        }

        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.message || errorData.error || 'Unauthorized',
          401
        );
      }

      const errorData = await response.json().catch(() => ({}));
      safeConsole.error(`[API Error ${requestId}]`, response.status, errorData);

      if (response.status === 429) {
        const headerVal = response.headers.get('Retry-After');
        const raw = parseInt(headerVal ?? String(errorData.retryAfter ?? 60), 10);
        throw new RateLimitError(isNaN(raw) ? 60 : raw);
      }

      throw new ApiError(
        errorData.message || errorData.error || 'An error occurred',
        response.status
      );
    }

    const responseData = await response.json();
    safeConsole.log(`[API Response ${requestId}] Success`);

    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
      return { data: responseData.data as T };
    }
    return { data: responseData as T };
  } catch (error) {
    if (error instanceof RateLimitError) throw error;
    if (error instanceof ApiError) {
      return { error: getFriendlyErrorMessage(error.message) };
    }
    safeConsole.error(`[API Network Error ${requestId}]`, error);
    return { error: 'Network error. Please check your connection.' };
  }
}

export async function fetchPatientApi<T>(
  endpoint: string,
  options: RequestInit & { skipAuth?: boolean; _retried?: boolean } = {}
): Promise<ApiResponse<T>> {
  const token = patientStorage.getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Request-ID': generateRequestId(),
    'X-Client-Version': '1.0.0',
    'X-App-Environment': APP_ENV,
    ...(options.headers as Record<string, string> | undefined),
  };

  if (token && !options.skipAuth) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Which linked clinic the patient is currently viewing — defaults to their primary clinic (the JWT
  // claim) when unset. See PatientTenantContextFilter on the backend.
  const activePatientClinicId = sessionStorage.getItem('active_patient_clinic_id');
  if (activePatientClinicId) headers['X-Active-Patient-Clinic-Id'] = activePatientClinicId;

  // Which linked dependent (child/parent/spouse) the account holder is currently viewing, if any.
  // See PatientDependentContextFilter on the backend — unlike the clinic header above, an invalid/
  // revoked/aged-out id here FAILS CLOSED (403), never silently falls back to the account holder's
  // own records.
  const activeDependentPatientId = sessionStorage.getItem('active_dependent_patient_id');
  const dependentContextActive = Boolean(activeDependentPatientId) && !endpoint.startsWith('/api/patient/dependents');
  if (dependentContextActive) headers['X-Active-Dependent-Patient-Id'] = activeDependentPatientId as string;

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        const isLoginEndpoint = endpoint === '/api/patient/auth/login';
        const isRefreshEndpoint = endpoint === '/api/patient/auth/refresh';
        const isLogoutEndpoint = endpoint === '/api/patient/auth/logout';

        if (!isLoginEndpoint && !isRefreshEndpoint && !isLogoutEndpoint && !options._retried) {
          const refreshed = await attemptPatientRefresh();
          if (refreshed) {
            return fetchPatientApi<T>(endpoint, { ...options, _retried: true });
          }
        }

        if (!isLoginEndpoint) {
          patientStorage.removeAuthToken();
          window.location.href = '/patient/login';
        }
        throw new ApiError('Unauthorized', 401);
      }
      if (response.status === 403 && dependentContextActive) {
        // Fail-closed denial: the switched dependent context is no longer valid (revoked, aged
        // out, unlinked from another tab). Clear it and send the user back to a safe screen
        // rather than let every subsequent call under this tab keep 403ing silently.
        sessionStorage.removeItem('active_dependent_patient_id');
        window.location.href = '/patient/family';
        throw new ApiError('Dependent access no longer valid', 403);
      }
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 429) {
        const headerVal = response.headers.get('Retry-After');
        const raw = parseInt(headerVal ?? String(errorData.retryAfter ?? 60), 10);
        throw new RateLimitError(isNaN(raw) ? 60 : raw);
      }

      throw new ApiError(
        errorData.message || errorData.error || 'An error occurred',
        response.status
      );
    }

    const responseData = await response.json();
    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
      return { data: responseData.data as T };
    }
    return { data: responseData as T };
  } catch (error) {
    if (error instanceof RateLimitError) throw error;
    if (error instanceof ApiError) {
      return { error: getFriendlyErrorMessage(error.message) };
    }
    return { error: 'Network error. Please check your connection.' };
  }
}

/** POST to an endpoint, download the response as a Blob. */
export async function fetchFileApi(
  endpoint: string,
  body: unknown
): Promise<ApiResponse<{ blob: Blob; fileName?: string }>> {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...baseHeaders() },
      body: JSON.stringify(body),
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) throw new ApiError('Unauthorized', 401);
      const isJson = response.headers.get('content-type')?.includes('application/json');
      const err = isJson ? await response.json().catch(() => ({})) : {};
      throw new ApiError(
        (err as any).message || (err as any).error || 'An error occurred',
        response.status
      );
    }

    const blob = await response.blob();
    return { data: { blob, fileName: extractFileName(response.headers.get('content-disposition')) } };
  } catch (error) {
    if (error instanceof ApiError) return { error: getFriendlyErrorMessage(error.message) };
    return { error: 'Network error. Please check your connection.' };
  }
}

/**
 * POST multipart/form-data — does NOT set Content-Type so the browser
 * automatically adds the boundary. Includes all standard auth headers.
 */
export async function fetchMultipartApi<T>(
  endpoint: string,
  body: FormData
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'X-Request-ID': generateRequestId(),
    'X-Client-Version': '1.0.0',
    'X-App-Environment': APP_ENV,
  };

  const sessionToken = sessionStorage.getItem('sessionToken');
  if (sessionToken) headers['X-Session-Token'] = sessionToken;

  const activeClinicId = sessionStorage.getItem('active_clinic_id');
  if (activeClinicId) headers['X-Active-Clinic-Id'] = activeClinicId;

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body,
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) throw new ApiError('Unauthorized', 401);
      const isJson = response.headers.get('content-type')?.includes('application/json');
      const err = isJson ? await response.json().catch(() => ({})) : {};
      throw new ApiError(
        (err as any).message || (err as any).error || 'An error occurred',
        response.status
      );
    }

    const data = await response.json().catch(() => undefined);
    return { data: data as T };
  } catch (error) {
    if (error instanceof ApiError) return { error: getFriendlyErrorMessage(error.message) };
    return { error: 'Network error. Please check your connection.' };
  }
}

/** GET an endpoint, download the response as a Blob. */
export async function fetchGetFileApi(
  endpoint: string
): Promise<ApiResponse<{ blob: Blob; fileName?: string }>> {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: baseHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) throw new ApiError('Unauthorized', 401);
      const isJson = response.headers.get('content-type')?.includes('application/json');
      const err = isJson ? await response.json().catch(() => ({})) : {};
      throw new ApiError(
        (err as any).message || (err as any).error || 'An error occurred',
        response.status
      );
    }

    const blob = await response.blob();
    return { data: { blob, fileName: extractFileName(response.headers.get('content-disposition')) } };
  } catch (error) {
    if (error instanceof ApiError) return { error: getFriendlyErrorMessage(error.message) };
    return { error: 'Network error. Please check your connection.' };
  }
}
